import { lazy, Suspense, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import type { ELK, ElkExtendedEdge, ElkNode } from 'elkjs';
import { nanoid } from 'nanoid';
import {
  faChevronLeft,
  faDiagramProject,
} from '@fortawesome/pro-regular-svg-icons';

import { AnyObject, DateFormat, Flow, Job } from '@typings';
import { API_ROOT, JWT_TOKEN, PLATFORM_API_URL } from '@constants';
import { useHelmetTitle } from '@hooks';
import { as, isArray, localStorage, path, tv } from '@utils';

import { ChaseSpinner, Helmet, Icon, Link, Modal, Render } from '@components';
import { Layout } from '@components/Layouts';
import { BakeModal } from '@components/Modals';
import { BakeParentNode, EmptyContent } from '@components/Ui';

const FlowCanvas = lazy(async () => {
  const [mod] = await Promise.all([
    import('reactflow'),
    import('reactflow/dist/style.css'),
  ]);

  const ReactFlow = mod.default;
  const { ReactFlowProvider } = mod;

  const Comp = (props: any) => (
    <ReactFlowProvider>
      <ReactFlow {...props} />
    </ReactFlowProvider>
  );

  return { default: Comp };
});

let elkInstance: ELK | null = null;

const getElk = async () => {
  if (!elkInstance) {
    const { default: ELK } = await import('elkjs/lib/elk.bundled');
    elkInstance = new ELK();
  }
  return elkInstance;
};

const variants = tv({
  base: 'border-0 rounded-xl bg-neural-02 text-h5 flex items-center justify-center',
  variants: {
    status: {
      pending: 'bg-warning-light',
      succeeded: 'bg-success',
      failed: 'bg-error-subtle',
      cancelled: 'bg-error-subtle',
      running: 'bg-success-light',
      suspended: 'bg-warning-subtle',
      cached: 'bg-success-light',
    },
    parent: {
      true: 'bg-opacity-50',
    },
  },
});

const NODE = {
  WIDTH: 225,
  HEIGHT: 84,
};

type Task = {
  jobId: string | null;
  outputs: AnyObject;
  status: Job.Status;
  createdAt: DateFormat;
  label?: string;
};

type Tasks = {
  [taskId: string]: Task;
};

const makeNode = ({
  isParent = false,
  nodeName,
  parentNode,
  width = isParent ? NODE.WIDTH * 1.5 : NODE.WIDTH,
  height = isParent ? NODE.HEIGHT * 2 : NODE.HEIGHT,
  x,
  y,
  status,
  createdAt,
  outputs,
  jobId,
  ...props
}: Partial<Task> & {
  nodeName: string;
  parentNode?: string;
  isParent?: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}) => {
  const label = parentNode ? nodeName.split(`${parentNode}.`)[1] : nodeName;
  const position = x && y ? { x, y } : undefined;

  return {
    ...props,
    ...(isParent && { type: 'parent-node' }),
    ...(parentNode && { parentNode }),
    id: nodeName,
    width,
    height,
    data: { label, status, createdAt, outputs, jobId },
    position,
    sourcePosition: 'right',
    targetPosition: 'left',
    style: { width, height },
    className: variants({ parent: isParent, status }),
  };
};

const makeEdge = (source: string, target: string): ElkExtendedEdge => ({
  id: `${source}-${target}`,
  sources: [source],
  targets: [target],
});

const createEdges = (graphs: Flow.BakeGraphs): ElkNode[] =>
  Object.values(graphs)
    .map((naming) =>
      Object.entries(naming).map(([targetName, sourceNames]) =>
        sourceNames.map((sourceName) => makeEdge(sourceName, targetName)),
      ),
    )
    .flat(2);

const createNodes = (graphs: Flow.BakeGraphs, tasks: Tasks) => {
  /**
   * Graphs board to make the connections and relatives
   */
  const graphsBoard = graphs[''];

  return Object.keys(graphsBoard).map((nodeName) => {
    const children = graphs[nodeName];
    const isParent = !!children;
    const node = makeNode({ ...tasks[nodeName], nodeName, isParent });

    if (children) {
      return {
        ...node,
        children: Object.keys(children).map((name) =>
          makeNode({
            ...tasks[name],
            nodeName: name,
            parentNode: nodeName,
          }),
        ),
      };
    }

    return node;
  });
};

const nodeTypes = {
  'parent-node': BakeParentNode,
};

export default function FlowBakeTasksPage() {
  const { projectId = '', flowName = '', bakeId = '' } = useParams();

  const { makeTitle } = useHelmetTitle();

  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadingElk, setLoadingElk] = useState(true);
  const [task, setTask] = useState<Task | null>();
  const [graph, setGraph] = useState<any>(null);

  useEffect(() => {
    const run = async () => {
      if (!graph) {
        return;
      }

      setLoadingElk(true);

      try {
        const elk = await getElk();
        const layout = await elk.layout(graph);

        if (!layout.children) {
          setNodes([]);
          setEdges([]);
          setLoadingElk(false);
          return;
        }

        const nodes = layout.children.reduce(
          (result, { children, ...node }) => {
            const { id: nodeName, x, y, width, height } = node;
            const { data } = node as any;

            const isParent = isArray(children) && !!children.length;

            result.push(
              makeNode({ ...data, nodeName, x, y, width, height, isParent }),
            );

            if (isParent) {
              children.forEach((child: any) => {
                const { id, x, y, width, height, data } = child;

                result.push(
                  makeNode({
                    ...data,
                    nodeName: id,
                    parentNode: nodeName,
                    x,
                    y,
                    width,
                    height,
                  }),
                );
              });
            }

            return result;
          },
          [] as any[],
        );

        // @ts-expect-error occued due to Elk and Reactflow types conflict
        const edges = graph.edges.map(({ id, sources, targets }) => ({
          id,
          source: sources[0],
          target: targets[0],
        }));

        setNodes(nodes);
        setEdges(edges);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
      } finally {
        setLoadingElk(false);
      }
    };

    run();
  }, [graph]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);

      try {
        const token = localStorage.get<string>(JWT_TOKEN);
        const headers = { Authorization: `Bearer ${token}` };

        const {
          data: {
            graphs,
            // @ts-expect-error forced to skip axios interceptor
            last_attempt: { id: lastAttemptId },
          },
        } = await axios.get<Flow.Bake>(
          path.create(PLATFORM_API_URL, API_ROOT, 'flow/bakes', bakeId),
          {
            params: { fetch_last_attempt: true },
            headers,
          },
        );

        const { data: tasks } = await axios.get<Array<Flow.BakeTask>>(
          path.create(PLATFORM_API_URL, API_ROOT, 'flow/tasks'),
          {
            params: { attempt_id: lastAttemptId },
            headers,
          },
        );

        const normalizedTasks: Tasks = Object.fromEntries(
          tasks.map(
            ({
              // @ts-expect-error forced to skip axios interceptor
              yaml_id: id,
              // @ts-expect-error forced to skip axios interceptor
              raw_id: jobId,
              outputs,
              statuses,
            }): [string, Task] => {
              // @ts-expect-error forced to skip axios interceptor
              const { status, created_at: createdAt } = as.o<Job.History>(
                statuses.at(-1),
              );
              return [id, { jobId, outputs: as.o(outputs), status, createdAt }];
            },
          ),
        );

        if (Object.keys(normalizedTasks).length === 0) {
          setGraph(null);
          setLoading(false);
          setLoadingElk(false);
          return;
        }

        const nodes = createNodes(graphs, normalizedTasks);
        const edges = createEdges(graphs);

        setGraph({
          id: 'root',
          layoutOptions: {
            'elk.algorithm': 'org.eclipse.elk.layered',
            'elk.direction': 'RIGHT',
            'spacing.nodeNodeBetweenLayers': 88,
            'elk.edgeRouting': 'POLYLINE',
            'elk.spacing.nodeNode': 40,
            'org.eclipse.elk.spacing.componentComponent': 64,
          },
          children: nodes,
          edges,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [bakeId]);

  // @ts-expect-error skipping `event`
  const handleNodeClick = (event, { data }: Node) => {
    setTask(data as Task);
  };

  return (
    <Layout>
      <Helmet title={makeTitle('Bake', flowName, 'Flows', '%p', '%c')} />

      <div slot="header" className="flex min-w-0 items-center gap-4">
        <Link
          variant="ghost"
          to={path.flowBakes(projectId, flowName)}
          className="h-auto p-0 text-[24px] text-neural-03"
        >
          <Icon icon={faChevronLeft} className="h-10 w-10" />
        </Link>
        <h3 className="truncate text-h4 text-white">{flowName}</h3>
      </div>

      {task?.status && (
        <Modal
          defaultOpen
          preventOutsideClick
          key={nanoid()}
          onClose={() => setTask(null)}
          content={<BakeModal {...task} />}
        />
      )}

      <Layout.Content className="flex flex-1 items-center justify-center px-0 pb-0 pt-24">
        <Render if={!loadingElk && graph}>
          <Suspense
            fallback={<ChaseSpinner color="primary" className="h-14 w-14" />}
          >
            <FlowCanvas
              fitView
              draggable={false}
              minZoom={0.01}
              maxZoom={0.75}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodeClick={handleNodeClick}
              defaultEdgeOptions={{
                zIndex: 100,
                markerEnd: { type: 'arrowclosed' },
                style: { strokeWidth: 2, stroke: '#79797B' },
              }}
            />
          </Suspense>
        </Render>

        <Render if={loading || loadingElk}>
          <ChaseSpinner color="primary" className="h-14 w-14" />
        </Render>

        <Render if={!loading && graph === null}>
          <EmptyContent
            className="pb-24 pt-6"
            icon={faDiagramProject}
            title="No tasks found"
          />
        </Render>
      </Layout.Content>
    </Layout>
  );
}
